import { useNavigate } from "react-router-dom";
import { LogOut, HelpCircle } from "lucide-react";
import { useAppDispatch } from "../../redux/hooks";
import { logout } from "../auth/AuthSlice";
import Joyride, { CallBackProps, STATUS, Step, ACTIONS } from 'react-joyride';
import { TourTooltip } from "../common/TourTooltip";
import { useState, useEffect } from "react";

const Home = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const handleLogout = () => {
    dispatch(logout());
  };

  // Tour state
  const [runTour, setRunTour] = useState(false);
  const tourSteps: Step[] = [
    {
      target: '.tour-scan-card',
      title: 'Start Scanning',
      content: 'Begin here to upload or capture images of answer sheets for AI evaluation.',
      disableBeacon: true,
    },
    {
      target: '.tour-eval-card',
      title: 'View Results',
      content: 'Read detailed reports, grades, & feedback for all your processed submissions.',
      disableBeacon: true,
    }
  ];

  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenHomeTour');
    if (!hasSeenTour) {
      setRunTour(true);
    }
  }, []);

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status, action } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status) || action === ACTIONS.CLOSE) {
      setRunTour(false);
      localStorage.setItem('hasSeenHomeTour', 'true');
    }
  };

  return (
    <div className=" bg-gray-50/30 text-gray-900 flex flex-col items-center justify-center p-6 overflow-hidden relative selection:bg-primary/20">
      {/* Background Gradients */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10 pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[70vw] h-[70vw] bg-gray-50/50 rounded-full blur-[100px]" />
        <div className="absolute top-[40%] -right-[10%] w-[60vw] h-[60vw] bg-gray-50/50 rounded-full blur-[100px]" />
      </div>

      {/* Logout Button */}
      <div className="flex w-full justify-end mb-4 z-50 gap-2">
        <button
          onClick={() => setRunTour(true)}
          className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:text-primary hover:bg-primary/5 hover:border-primary/10 transition-all shadow-sm"
        >
          <HelpCircle className="w-4 h-4" />
          <span>Tutorial</span>
        </button>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:text-red-600 hover:bg-red-50 hover:border-red-100 transition-all shadow-sm"
        >
          <LogOut className="w-4 h-4" />
          <span>Logout</span>
        </button>
      </div>

      <div className="text-center max-w-2xl mx-auto mb-16 space-y-4">
        <h1 className="text-5xl md:text-7xl font-black tracking-tighter text-gray-900 leading-tight">
          AI Copy <span className="text-primary">Evaluator</span>
        </h1>
        <p className="text-xl text-gray-500 font-medium max-w-lg mx-auto leading-relaxed">
          Transform your physical answer sheets into detailed digital insights with the power of Artificial Intelligence.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 w-full max-w-4xl">
        {/* Scanning Card */}
        <button
          onClick={() => navigate('/scan')}
          className="tour-scan-card group relative bg-white border-primary h-full rounded-3xl p-8 border border-gray-100 shadow-2xl shadow-gray-100 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/20 transition-all duration-500 text-left overflow-hidden h-[320px] flex flex-col"
        >
          <div className="absolute top-0 right-0 p-32 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-2xl -mr-16 -mt-16 group-hover:bg-primary/10 transition-colors" />

          {/* Icon / Animation Area */}
          <div className="flex-1 flex items-center justify-center relative mb-4">
            <div className="relative w-32 h-44 bg-white border-2 border-gray-200 rounded-lg shadow-sm flex flex-col gap-2 p-3 overflow-hidden group-hover:shadow-md transition-shadow">
              <img className="w-44 animate-pulse-subtle" src="/scanner.webp" alt="Scanner icon" />
            </div>
            {/* Sparkles */}
            <div className="absolute -right-4 top-1/2 text-primary animate-pulse"></div>
          </div>

          <div className="relative z-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">Start Scanning</h3>
            <p className="text-gray-500 font-medium leading-relaxed">Upload or scan physical answer sheets to begin the evaluation process.</p>
          </div>
        </button>

        {/* Evaluation Card */}
        <button
          onClick={() => navigate('/evaluator')}
          className="tour-eval-card group relative bg-white border-primary h-full rounded-3xl p-8 border border-gray-100 shadow-2xl shadow-gray-100 hover:shadow-xl hover:shadow-primary/5 hover:border-primary/20 transition-all duration-500 text-left overflow-hidden h-[320px] flex flex-col"
        >
          <div className="absolute top-0 right-0 p-32 bg-gradient-to-br from-primary/5 to-transparent rounded-full blur-2xl -mr-16 -mt-16 group-hover:bg-primary/10 transition-colors" />

          {/* Icon / Animation Area */}
          <div className="flex-1 flex items-center justify-center relative mb-4">
            <div className="relative w-32 h-44 bg-white border-2 border-gray-200 rounded-lg shadow-sm flex flex-col gap-2 p-3 group-hover:translate-y-[-4px] transition-transform duration-500 group-hover:shadow-md">
              <img className="w-44 animate-pulse-subtle" src="/evaluation1.webp" alt="Evaluation icon" />

            </div>
          </div>

          <div className="relative z-10">
            <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-primary transition-colors">Evaluations</h3>
            <p className="text-gray-500 font-medium leading-relaxed">Read detailed reports, scores, & AI feedback for previous submissions.</p>
          </div>
        </button>
      </div>


      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        tooltipComponent={TourTooltip}
        callback={handleJoyrideCallback}
        disableScrollParentFix={true}
        disableScrolling={true}
        floaterProps={{
          disableAnimation: true,
        }}
        styles={{
          spotlight: {
            borderRadius: '24px',
          },
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
          },
          options: {
            zIndex: 10000,
          }
        }}
      />
    </div>
  );
};

export default Home;
